import React, {useRef, useState, useEffect, useMemo} from 'react';
import {Formik, Form, FormikProps, useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import InnerPop from '../../../../component/newPopup/innerPop/InnerPop';
import Input from '../../../../component/input/Input';
import NumberInput from '../../../../component/input/NumberInput';
import {TShow} from '../../../../component/prompotionPopup/PromotionPopup';
import {getPricingGroupValidationSchema} from './PricingGroupPop.validation';
import {getPriceGroupImpact, updatePriceGroup} from '../../../../redux/subscription/subscriptionActions';
import {bounceMessage} from '../../../../layout/userMessages/UserMessage';
import {PriceGroup} from '../../../../redux/subscription/subscription.type';
import * as css from './PricingGroupPop.css';

interface PricingGroupFormValues {
    name: string;
    startPrice: string;
    endPrice: string;
}

interface PricingGroupPopProps {
    show: TShow;
    onSave?: (values: PricingGroupFormValues) => void | Promise<void>;
    initialValues?: Partial<PricingGroupFormValues>;
    priceGroupId?: number;
    priceGroups?: PriceGroup[] | null;
}

function PricingGroupPop({
    show,
    onSave,
    initialValues: propInitialValues,
    priceGroupId,
    priceGroups
}: PricingGroupPopProps) {
    const {t} = useTranslation('subscriptions');
    const {t: globalT} = useTranslation('global');
    const formRef = useRef<FormikProps<PricingGroupFormValues>>(null);
    const [impactInfo, setImpactInfo] = useState<{affectedPlans: number} | null>(null);
    
    // Get redemptionPrice and redemptionUpToPrice from parent Formik context
    const parentFormik = useFormikContext<any>();
    const redemptionPrice = parentFormik?.values?.redemptionPrice || null;
    const redemptionUpToPrice = parentFormik?.values?.redemptionUpToPrice || null;

    const validationSchema = getPricingGroupValidationSchema(globalT);

    const formInitialValues: PricingGroupFormValues = useMemo(() => {
        if(propInitialValues) {
            return {
                name: propInitialValues.name || '',
                startPrice: propInitialValues.startPrice || '',
                endPrice: propInitialValues.endPrice || ''
            };
        }

        if(priceGroupId) {
            const priceGroup = (priceGroups || []).find((pg: PriceGroup) => pg.spgId === priceGroupId);
            if(priceGroup) {
                return {
                    name: priceGroup.title,
                    startPrice: priceGroup.minPrice?.toString() || '',
                    endPrice: priceGroup.maxPrice?.toString() || ''
                };
            }
        }

        return {
            name: '',
            startPrice: redemptionPrice || '',
            endPrice: redemptionUpToPrice || ''
        };
    }, [propInitialValues, priceGroupId, priceGroups, redemptionPrice, redemptionUpToPrice]);

    useEffect(() => {
        // Load impact info if editing existing price group
        if(priceGroupId) {
            getPriceGroupImpact(priceGroupId).then((res) => {
                if(res?.affectedPlans !== undefined) {
                    setImpactInfo({affectedPlans: res.affectedPlans});
                }
            })
        }
    }, []);

    const handleSave = async (values: PricingGroupFormValues, {setSubmitting}: any) => {
        // If editing existing price group and there are affected plans, show warning
        if(priceGroupId && impactInfo && impactInfo.affectedPlans > 0) {
            setSubmitting(false);
            bounceMessage(
                t('priceGroupUpdateWarning', {count: impactInfo.affectedPlans}),
                'choice',
                {
                    text: t('continue'),
                    onClick: async () => {
                        await performUpdate(values);
                    }
                },
                {
                    text: t('cancel')
                }
            );
        } else {
            // No warning needed - either new group or no affected plans
            if(onSave) {
                await onSave(values);
            }
            show.close();
        }
    };

    const performUpdate = async (values: PricingGroupFormValues) => {
        if(priceGroupId) {
            try {
                const minPrice = parseFloat(values.startPrice);
                const maxPrice = parseFloat(values.endPrice);
                await updatePriceGroup(priceGroupId, {
                    name: values.name,
                    minPrice,
                    maxPrice
                });
                if(onSave) {
                    await onSave(values);
                }
                show.close();
            } catch (error) {
                console.error('Error updating price group:', error);
            }
        } else if(onSave) {
            await onSave(values);
            show.close();
        }
    };

    const handleCancel = () => {
        formRef.current?.resetForm();
        show.close();
    };

    return (
        <css.pricingGroupPop>
            <Formik
                innerRef={formRef}
                initialValues={formInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleSave}
                enableReinitialize
            >
                <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <InnerPop
                        dataCy='pricingGroup'
                        show={show}
                        title={priceGroupId ? t('editPriceGroup') : t('pricingGroup')}
                        buttons={{
                            whiteBtn: {
                                text: t('cancel'),
                                onClick: handleCancel
                            },
                            blueBtn: {
                                text: t('save'),
                                onClick: () => formRef.current?.submitForm()
                            }
                        }}
                    >
                        <css.header />
                        <css.content>
                            <Input
                                name='name'
                                text={t('pricingGroupName')}
                                description={t('pricingGroupNameDescription')}
                                icon={null}
                                events={{}}
                                className=''
                                forwardRef={null}
                            />
                            <NumberInput
                                name='startPrice'
                                text={t('startPrice')}
                                icon={null}
                                className=''
                                forwardRef={null}
                            />
                            <NumberInput
                                name='endPrice'
                                text={t('endPrice')}
                                icon={null}
                                className=''
                                forwardRef={null}
                            />
                        </css.content>
                    </InnerPop>
                </Form>
            </Formik>
        </css.pricingGroupPop>
    );
}

export default PricingGroupPop;

